/**
 * The ASSIST frame, the same as ASSIST-MNH's ("Meridian"): a white header; a navy side navigation
 * on desktop that becomes an icon rail on a tablet; bottom tabs on a phone. Three zones on a
 * patient's screens: WHERE AM I (side navigation) · WHO AM I WITH (the patient bar) · WHAT AM I
 * DOING WITH HER OR HIM (the step strip). Presentation only: nothing here writes clinical data.
 */
import {
  type EpisodeOfCare,
  type FormDef,
  type Pack,
  type Patient,
  type PatientRecord,
  SYS_PROGRAMME,
  type WorklistEntry,
  activeProgrammes,
  daysBetween,
  evalCondition,
  formOfEncounter,
  motherOf,
  openEpisodes,
  splitRecord,
  worklist,
} from '@assist/core';
import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useBundles } from './care.js';
import { app, clock } from './controller.js';
import { useApp, useRoute } from './hooks.js';
import { t } from './i18n.js';
import { type ActivePack, activePacks, allPacks } from './packs.js';
import { ageOf, displayName, mrnOf } from './patient.js';
import { HUE, Icon, Mark, packIcon } from './ui/icons.js';

// ------------------------------------------------------------------ where am I

/** Every programme of the active packs, with its pack: one navigation item and tile each. */
export function activeProgrammeList(
  packs: Pack[],
): Array<{ pack: Pack; programme: Pack['programmes'][number] }> {
  return packs.flatMap((pack) => pack.programmes.map((programme) => ({ pack, programme })));
}
export const programmeTitle = (p: Pack['programmes'][number]) => p.list_title ?? p.display;
export const programmeShort = (p: Pack['programmes'][number]) =>
  p.short ?? p.display.split(' ')[0] ?? p.code;
export const programmeIcon = (pack: Pack, p: Pack['programmes'][number]) =>
  p.icon ?? packIcon(pack.id);

/** A pack's name without its qualifier: "Hypertension and type 2 diabetes". */
export const packName = (p: Pack) => p.short_title ?? p.title.replace(/\s*\(.*\)\s*$/, '');
/** Its programmes' codes, for the icon rail: "HTN·DM". */
export const packShort = (p: Pack) =>
  p.programmes.map((x) => x.code.toUpperCase()).join('·') || packName(p).split(' ')[0] || '';

/** The navigation item a route belongs to. */
export function activeHref(r: string[]): string {
  const [a, b] = r;
  if (!a) return '#/';
  if (a === 'register') return '#/register';
  // a visit belongs to its programme; the rest of a patient's screens to "Find a patient"
  if (a === 'patient') {
    // a visit belongs to the programme of its form
    const form =
      r[2] === 'visit' && r[3]
        ? allPacks.find((p) => p.id === r[3])?.forms.find((f) => f.id === r[4])
        : undefined;
    const prog = [form?.requires ?? []].flat()[0] ?? form?.enrols ?? form?.programmes[0];
    return prog ? `#/programme/${prog}` : '#/find';
  }
  if (a === 'hmis') return '#/hmis';
  if (a === 'programme' && b) return `#/programme/${b}`;
  return `#/${a}`;
}

/** Counts shown on the navigation and the home tiles. */
export interface CareCounts {
  entries: WorklistEntry[];
  overdue: number;
  due: number;
  inCare: Record<string, number>;
}

export function useCounts(): CareCounts | null {
  const a = useApp();
  const bundles = useBundles();
  if (!bundles) return null;
  const packs = activePacks(a.packSettings).map((x) => x.pack);
  const today = clock.today();
  const entries = worklist(bundles, packs, today);
  const inCare: Record<string, number> = {};
  for (const { pack, programme } of activeProgrammeList(packs))
    inCare[programme.code] = programme.episode
      ? openEpisodes(bundles, [programme]).length
      : worklist(bundles, [{ ...pack, programmes: [programme] }], today).length;
  return {
    entries,
    overdue: entries.filter((e) => e.status.state === 'overdue').length,
    due: entries.filter((e) => e.status.state === 'due').length,
    inCare,
  };
}

function NavItem(props: {
  href: string;
  icon: string;
  label: string;
  short: string;
  on: boolean;
  count?: number | undefined;
}) {
  return (
    <a
      class={props.on ? 'nv on' : 'nv'}
      href={props.href}
      title={props.label}
      aria-current={props.on ? 'page' : undefined}
    >
      <Icon k={props.icon} />
      <span class="lb">{props.label}</span>
      <span class="sh">{props.short}</span>
      {props.count ? <span class="cnt">{props.count}</span> : null}
    </a>
  );
}

export function Side() {
  const a = useApp();
  const r = useRoute();
  const counts = useCounts();
  const here = activeHref(r);
  const u = a.session?.user;
  if (!u) return null;
  const packs = activePacks(a.packSettings).map((x) => x.pack);
  const it = (href: string, icon: string, label: string, short: string, count?: number) => (
    <NavItem
      key={href}
      href={href}
      icon={icon}
      label={label}
      short={short}
      on={here === href}
      count={count}
    />
  );
  return (
    <aside id="side">
      <a class="sbrand" href="#/">
        <span class="mk">
          <Mark />
        </span>
        <span class="bt">
          {t.appName}
          <small>{u.facilityName || t.tagline}</small>
        </span>
      </a>
      <nav class="snav" aria-label={t.mainNav}>
        <div class="grp">{t.grpCare}</div>
        {it('#/', 'today', t.navHome, t.shHome)}
        {it('#/register', 'register', t.registerPatient, t.shRegister)}
        {it('#/find', 'find', t.navFind, t.shFind)}
        {it('#/followup', 'worklists', t.navFollowup, t.shFollowup, counts?.overdue)}
        {packs.length ? <div class="grp">{t.grpProgrammes}</div> : null}
        {activeProgrammeList(packs).map(({ pack, programme }) =>
          it(
            `#/programme/${programme.code}`,
            programmeIcon(pack, programme),
            programmeTitle(programme),
            programmeShort(programme),
          ),
        )}
        <div class="grp">{t.grpReports}</div>
        {it('#/numbers', 'dashboard', t.navNumbers, t.shNumbers)}
        {it('#/hmis', 'registers', t.hmisTitle, t.shHmis)}
        {packs.some((p) => p.registers?.length)
          ? it('#/registers', 'export', t.registersTitle, t.shRegisters)
          : null}
        {u.role === 'supervisor' || u.role === 'admin'
          ? it('#/supervisor', 'dashboard', t.supervisorTitle, t.shBoard)
          : null}
        {u.role === 'admin' ? <div class="grp">{t.grpAdmin}</div> : null}
        {u.role === 'admin' ? it('#/settings', 'admin', t.settings, t.settings) : null}
        <div class="grp">{t.grpYou}</div>
        {a.status?.failed
          ? it('#/failed', 'sync', t.failedTitle, t.shRefused, a.status.failed)
          : null}
        {it('#/me', 'account', t.navMe, t.shMe)}
      </nav>
    </aside>
  );
}

/** Phones: five tabs at the bottom instead of the side navigation. */
export function BotNav() {
  const r = useRoute();
  const here = activeHref(r);
  const report = here === '#/numbers' || here === '#/hmis' || here === '#/registers';
  const B = (href: string, icon: string, label: string, on = here === href) => (
    <a class={on ? 'bn on' : 'bn'} href={href} aria-current={on ? 'page' : undefined}>
      <Icon k={icon} size={23} />
      <span>{label}</span>
    </a>
  );
  return (
    <nav class="botnav" aria-label={t.mainNav}>
      {B('#/', 'today', t.navHome)}
      {B('#/find', 'find', t.shFind)}
      {B('#/followup', 'worklists', t.shFollowup)}
      {B('#/numbers', 'dashboard', t.shReports, report)}
      {B('#/me', 'account', t.shMe)}
    </nav>
  );
}

export function Header() {
  const a = useApp();
  const u = a.session?.user;
  return (
    <header id="top">
      <a href="#/" id="brandhome" title={t.navHome}>
        <b>
          <span class="brand-mark">
            <Mark size={16} />
          </span>
          <span>{t.appName}</span>
        </b>
      </a>
      {u ? (
        <span id="who">
          {u.fullName} [{t.roleName[u.role] ?? u.role}]
          {u.facilityName ? ` · ${u.facilityName}` : ''}
        </span>
      ) : null}
      {u ? <SyncPill /> : null}
      {u ? (
        <button type="button" id="logout" onClick={() => app.lock()}>
          {t.lock}
        </button>
      ) : null}
    </header>
  );
}

export function SyncPill() {
  const a = useApp();
  const st = a.status;
  let cls = 'green';
  let label: string = t.synced;
  if (a.syncing) {
    cls = 'amber';
    label = t.syncing;
  } else if (st && st.failed > 0) {
    cls = 'red';
    label = t.failedN(st.failed);
  } else if (!a.online) {
    cls = 'amber';
    label = st?.outbox ? `${t.offline} · ${t.pendingN(st.outbox)}` : t.offline;
  } else if (st && st.outbox > 0) {
    cls = 'amber';
    label = t.pendingN(st.outbox);
  }
  const last = st?.lastSyncAt
    ? new Date(st.lastSyncAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : t.never;
  return (
    <button
      type="button"
      id="net"
      class={`pill ${cls}`}
      onClick={() => void app.syncNow()}
      title={`${t.lastSync(last)} · ${t.syncNow}`}
    >
      {label}
    </button>
  );
}

// ------------------------------------------------------------------ home tiles

export function Tile(props: {
  href: string;
  icon: string;
  title: string;
  sub?: string | undefined;
  alert?: boolean;
  hue?: string;
}) {
  return (
    <a
      href={props.href}
      class={props.alert ? 'tile due' : 'tile'}
      style={{ '--h': props.hue ?? HUE[props.icon] ?? '#1B3A5C' }}
    >
      <span class="ti">
        <Icon k={props.icon} size={26} />
      </span>
      <span class="tx">
        <b>{props.title}</b>
        <small>{props.sub ?? ''}</small>
      </span>
      {props.alert ? (
        <span class="tbadge al" title={t.needsAttention}>
          <Icon k="highrisk" size={15} />
        </span>
      ) : null}
    </a>
  );
}

export const Section = ({ label, children }: { label: string; children: ComponentChildren }) => (
  <>
    <div class="tsect">{label}</div>
    <div class="tgrid">{children}</div>
  </>
);

/** A patient-hub action tile: icon over label, identity colour. */
export function HubTile(props: {
  href?: string;
  onClick?: () => void;
  icon: string;
  label: string;
  hue?: string;
}) {
  const style = { '--h': props.hue ?? HUE[props.icon] ?? '#1B3A5C' };
  const inner = (
    <>
      <Icon k={props.icon} size={30} />
      <span>{props.label}</span>
    </>
  );
  return props.href ? (
    <a class="hubx" href={props.href} style={style}>
      {inner}
    </a>
  ) : (
    <button type="button" class="hubx" style={style} onClick={props.onClick}>
      {inner}
    </button>
  );
}

// ------------------------------------------------------------------ who am I with

const initials = (p: Patient) =>
  (
    displayName(p)
      .split(/\s+/)
      .map((s) => s[0] ?? '')
      .join('')
      .slice(0, 2) || '—'
  ).toUpperCase();

const programmesOf = (episodes: EpisodeOfCare[]) =>
  episodes
    .filter((e) => e.status === 'active')
    .flatMap((e) => e.type.flatMap((x) => x.coding ?? []))
    .filter((c) => c.system === SYS_PROGRAMME)
    .map((c) => c.display ?? c.code ?? '');

/**
 * The sticky patient bar and the step strip, on every screen about one patient. `here` is the
 * step being shown: 'hub', 'edit' or 'visit:<pack id>/<form id>'.
 */
export function PatientChrome({ id, here }: { id: string; here: string }) {
  const a = useApp();
  const [p, setP] = useState<Patient | null>(null);
  const [rec, setRec] = useState<PatientRecord | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh when the store changes
  useEffect(() => {
    let live = true;
    void app.session?.get<Patient>('Patient', id).then((x) => live && setP(x));
    void app.session?.forPatient(id).then((rs) => live && setRec(splitRecord(rs)));
    return () => {
      live = false;
    };
  }, [id, a.status]);
  if (!p) return <div class="pbar pbar-empty" />;
  const age = ageOf(p, clock.today());
  const meta = [
    mrnOf(p) ? `MRN ${mrnOf(p)}` : '',
    p.gender === 'female' ? t.female : p.gender === 'male' ? t.male : '',
    age ? t.age(age) : '',
    ...programmesOf(rec?.episodes ?? []),
  ]
    .filter(Boolean)
    .join(' · ');
  const steps: Array<[string, string, string]> = [
    ['hub', `#/patient/${id}`, t.stepSummary],
    ...stepForms(formsFor(activePacks(a.packSettings), p, rec), rec).map(
      (f): [string, string, string] => [`visit:${f.pack.id}/${f.form.id}`, f.href, f.form.title],
    ),
    ['edit', `#/patient/${id}/edit`, t.stepDetails],
  ];
  return (
    <>
      <div class="pbar">
        <div class="pav">{initials(p)}</div>
        <div class="pinfo">
          <div class="pnm">{displayName(p)}</div>
          <div class="pmeta">{meta}</div>
        </div>
        {here !== 'hub' ? (
          <a class="pback" href={`#/patient/${id}`}>
            {t.hub}
          </a>
        ) : null}
      </div>
      <nav class="steps" aria-label={t.stepsNav}>
        {steps.map(([k, href, label]) => (
          <a
            key={k}
            class={k === here ? 'step on' : 'step'}
            href={href}
            aria-current={k === here ? 'step' : undefined}
          >
            {label}
          </a>
        ))}
      </nav>
    </>
  );
}

export interface AvailableForm {
  pack: Pack;
  form: FormDef;
  allowDraft: boolean;
  href: string;
  /** A once-per-episode form already filled in the open episode. */
  done: boolean;
}

/**
 * The forms this patient can be seen with now: who the form is for (sex, age), whether the episode
 * it needs is open, and whether a once-per-episode form is already done.
 */
export function formsFor(
  packs: ActivePack[],
  p: Patient,
  rec: PatientRecord | null,
): AvailableForm[] {
  const age = ageOf(p, clock.today());
  const ctx = {
    sex: p.gender ?? null,
    age: age ? age.years : null,
    age_days: p.birthDate?.length === 10 ? daysBetween(p.birthDate, clock.today()) : null,
    linked: !!motherOf(p),
  };
  const open = new Set(activeProgrammes(rec?.episodes ?? []));
  const out: AvailableForm[] = [];
  for (const { pack, allowDraft } of packs)
    for (const form of pack.forms) {
      if (!evalCondition(form.when, ctx)) continue;
      const req = [form.requires ?? []].flat();
      if (req.length && !req.some((r) => open.has(r))) continue;
      let done = false;
      if (form.once && rec) {
        const ep = rec.episodes.find(
          (e) =>
            e.status === 'active' &&
            e.type.some((x) =>
              x.coding?.some(
                (c) =>
                  c.system === SYS_PROGRAMME &&
                  [form.requires ?? [], form.enrols ?? []].flat().includes(c.code ?? ''),
              ),
            ),
        );
        done = rec.encounters.some(
          (e) =>
            formOfEncounter(e) === form.id &&
            (!ep || (e.episodeOfCare ?? []).some((r) => r.reference === `EpisodeOfCare/${ep.id}`)),
        );
      }
      out.push({
        pack,
        form,
        allowDraft,
        href: `#/patient/${p.id}/visit/${pack.id}/${form.id}`,
        done,
      });
    }
  return out;
}

/** The step strip: the forms of the episode she is in (all forms when none is open). */
export function stepForms(forms: AvailableForm[], rec: PatientRecord | null): AvailableForm[] {
  const open = new Set(activeProgrammes(rec?.episodes ?? []));
  const inEpisode = forms.filter(
    (f) =>
      [f.form.requires ?? []].flat().some((r) => open.has(r)) ||
      (!!f.form.enrols && open.has(f.form.enrols)),
  );
  return inEpisode.length ? inEpisode : forms.filter((f) => ![f.form.requires ?? []].flat().length);
}

/** The tile label: "New visit: …" for a pack's single visit form; otherwise the form's name. */
export const formLabel = (pack: Pack, form: FormDef = pack.forms[0] as FormDef) =>
  pack.forms.length === 1 && !form.series ? t.newVisit(form.title) : form.title;

/** Keep `body` classes in step with the session (the side navigation pads the page). */
export function useBodyClasses(signedIn: boolean) {
  useEffect(() => {
    document.body.classList.toggle('has-side', signedIn);
    document.body.classList.toggle('signed-out', !signedIn);
  }, [signedIn]);
}
